/**
 * A sugestão do protocolo pelos comprovantes lidos por imagem, para o formulário (E101.F5.T02).
 *
 * Só se pede quando o caso não tem protocolo gravado: protocolo salvo nunca é substituído pela
 * sugestão (ADR-0085 §2.2). Pede de novo quando a leitura dos documentos muda de estado na tabela
 * e, enquanto o servidor diz que ainda há comprovante sendo lido, a cada `ESPERA_DA_LEITURA_MS`,
 * até `TENTATIVAS_DA_LEITURA` vezes. A resposta é do caso que a pediu: trocar de caso descarta a
 * que chega tarde. Falha na leitura da sugestão deixa o formulário vazio, para digitar.
 */
import { useEffect, useState } from 'react';

import { useApiClient } from '@/lib/api';
import type { ExameEletronicoCasoData, ProtocoloAssinaturaCasoData } from '@/lib/exame_eletronico';
import { lerSugestaoPorImagem, protocoloDaSugestao } from '@/lib/exame_eletronico_importacao';
import { ESPERA_DA_LEITURA_MS, TENTATIVAS_DA_LEITURA } from '@/pages/useProcessamentoInicial';

export interface SugestaoPorImagem {
  sugestao: ProtocoloAssinaturaCasoData | null;
  lendo: boolean;
}

const NENHUMA: SugestaoPorImagem = { sugestao: null, lendo: false };

interface Lida {
  caseId: string;
  chave: string;
  tentativa: number;
  dados: SugestaoPorImagem;
}

export function useSugestaoPorImagem(
  caseId: string,
  caso: ExameEletronicoCasoData,
): SugestaoPorImagem {
  const client = useApiClient();
  const chave =
    caso.protocolo === null
      ? caso.pecas.map((p) => `${p.source_document_id}:${p.leitura_por_imagem ?? ''}`).join('|')
      : null;
  const [tentativa, setTentativa] = useState(0);
  const [lida, setLida] = useState<Lida | null>(null);
  // As tentativas são do caso: a troca recomeça a contagem (ajuste no render, não num efeito).
  const [casoDasTentativas, setCasoDasTentativas] = useState(caseId);
  if (casoDasTentativas !== caseId) {
    setCasoDasTentativas(caseId);
    setTentativa(0);
  }

  useEffect(() => {
    if (chave === null) return;
    let cancelado = false;
    const guardar = (dados: SugestaoPorImagem): void => {
      if (!cancelado) setLida({ caseId, chave, tentativa, dados });
    };
    lerSugestaoPorImagem(client, caseId)
      .then((resposta) =>
        guardar({
          sugestao: resposta.sugestao === null ? null : protocoloDaSugestao(resposta.sugestao),
          lendo: resposta.leitura_em_andamento,
        }),
      )
      .catch(() => guardar(NENHUMA));
    return () => {
      cancelado = true;
    };
  }, [client, caseId, chave, tentativa]);

  const vigente = chave !== null && lida !== null && lida.caseId === caseId && lida.chave === chave;
  const lendo = vigente && lida.dados.lendo;
  useEffect(() => {
    if (!lendo || tentativa >= TENTATIVAS_DA_LEITURA) return;
    const espera = setTimeout(() => setTentativa((n) => n + 1), ESPERA_DA_LEITURA_MS);
    return () => clearTimeout(espera);
  }, [lendo, tentativa]);

  return vigente ? lida.dados : NENHUMA;
}
